import { useState } from "react";
import { motion } from "framer-motion";
import { Award, Medal, FileCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";

const STEPS = [
  { icon: FileCheck, title: "Envíanos tu trofeo", text: "Rellena el formulario con la especie y los datos de la captura." },
  { icon: Award, title: "Medición oficial", text: "Nuestros medidores homologados valoran tu pieza según la fórmula oficial." },
  { icon: Medal, title: "Certificado y medalla", text: "Recibe tu diploma homologado y la medalla del club si tu pieza la merece." },
];

export default function HomologaTuTrofeo() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", species: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", { ...form, type: "homologacion" });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", species: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="homologa-page">
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1543782248-03e2c5a93e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxkZWVyJTIwc3RhZ3xlbnwwfHx8fDE3ODYwOTgwODh8MA&ixlib=rb-4.1.0&q=85" alt="Ciervo" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="overline-gold mb-4">Medición oficial</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight hero-text-shadow">
              Homologa Tu <span className="text-gold italic">Trofeo</span>
            </h1>
            <p className="mt-5 text-white/75 font-light max-w-xl hero-text-shadow">
              Da a tu captura el reconocimiento que merece. Medición oficial, certificado y medalla del club.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-20">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              className="border-l border-gold/30 pl-6" data-testid={`homologa-step-${i}`}>
              <s.icon size={26} strokeWidth={1.25} className="text-gold mb-4" />
              <h3 className="text-xl text-white font-medium">{s.title}</h3>
              <p className="mt-2 text-white/60 text-sm font-light leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <p className="overline-gold mb-4">Solicitud</p>
            <h2 className="text-3xl text-white font-light">Solicita tu homologación</h2>
            <p className="mt-4 text-white/60 text-sm font-light leading-relaxed">
              Cuéntanos qué has cazado y dónde. Te contactaremos por WhatsApp o email para agendar la medición
              con nuestro equipo de medidores homologados.
            </p>
            <div className="mt-8 card-dark p-6">
              <p className="text-sand text-sm font-medium">Servicio de homologación + medalla: 200 €</p>
              <p className="text-white/50 text-xs mt-2 font-light">Disponible en la tienda del club. Los socios tienen prioridad de agenda.</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="homologa-form">
            <input className="input-dark" placeholder="Nombre completo" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="homologa-name" />
            <input className="input-dark" type="email" placeholder="Email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="homologa-email" />
            <input className="input-dark" placeholder="Teléfono" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="homologa-phone" />
            <input className="input-dark" placeholder="Especie (ciervo, jabalí, corzo...)" value={form.species}
              onChange={(e) => setForm({ ...form, species: e.target.value })} data-testid="homologa-species" />
            <textarea className="input-dark min-h-[120px]" placeholder="Cuéntanos los detalles de la captura (fecha, finca, características...)" required
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="homologa-message" />
            <button type="submit" disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="homologa-submit">
              <Send size={15} /> {busy ? "Enviando..." : "Enviar Solicitud"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
